import { Alert, AlertContent, AlertText } from "@hortis/ui/alert";
import { CommandGroup, CommandLoading } from "@hortis/ui/command";
import { AlertTriangle } from "@hortis/ui/icons";
import { toast } from "@hortis/ui/sonner";
import { A } from "@mobily/ts-belt";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useRouteContext } from "@tanstack/react-router";
import { pipe } from "fp-ts/function";
import {
  type RecordTag,
  type TaxaListFieldsFragment,
  namedOperations,
  useCollectionSiteTagsQuery,
  useGetSiteTaxaQuery,
  useGetTaxaQuery,
  useOrganisationTagsQuery,
} from "generated/graphql";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  addTagToTaxaDocument,
  removeTagFromTaxaDocument,
} from "src/features/taxonomy/components/taxon-page/graphql";
import { useGqlMutation } from "src/utils/hooks/graphql";
import { CommandMenuTagsPage } from "../tags";
interface CommandMenuTaxonTagsPageProps {
  selectedRows: Array<TaxaListFieldsFragment>;
  search: string;
  selectedRecordsIDs: Array<string>;
  clearSearch: () => void;
  allTags: Array<Pick<RecordTag, "id" | "name">>;
  refetchTags: () => void;
}

export const ErrorAlert = ({ message }: { message: string }) => (
  <CommandGroup>
    <div className="p-3">
      <Alert color="warning" className="mb-4" data-cy="taxon-tags-alert">
        <AlertTriangle />
        <AlertContent>
          <AlertText>{message}</AlertText>
        </AlertContent>
      </Alert>
    </div>
  </CommandGroup>
);

const CommandMenuTaxonTagsPage = ({
  allTags,
  refetchTags,
  selectedRows,
  search,
  clearSearch,
  selectedRecordsIDs,
}: CommandMenuTaxonTagsPageProps) => {
  const queryClient = useQueryClient();
  const addTagToTaxa = useGqlMutation({
    mutation: addTagToTaxaDocument,
    onError: () => {
      toast.error("Failed to add tag to taxa");
    },
  });
  const removeTagFromTaxa = useGqlMutation({
    mutation: removeTagFromTaxaDocument,
    onError: () => {
      toast.error("Failed to remove tag from taxa");
    },
  });

  const res = useMemo(
    () =>
      pipe(
        selectedRows,
        A.map((record) => {
          const tags = record.tagsConnection?.nodes;
          return {
            recordId: record.id,
            tagIds: tags?.map((tag) => tag.id) ?? [],
          };
        }),
      ),
    [selectedRows],
  );

  const [activeRecordTags, setActiveRecordTags] = useState(res);

  useEffect(() => {
    setActiveRecordTags(res);
  }, [res]);

  const applyTagToSelectedRecords = useCallback(
    async (tagId: string) => {
      const res = await addTagToTaxa.mutateAsync({
        tagId,
        ids: selectedRecordsIDs,
      });

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Tests flake seeming to return nullable removeTagPlantMaterials
      const addTagSuccessful = res?.addTagTaxa?.success ?? false;
      if (addTagSuccessful) {
        toast.success(`Tag added to ${selectedRecordsIDs.length} taxa`);
        void queryClient.invalidateQueries({
          queryKey: [namedOperations.Query.GetTaxa],
        });
        void queryClient.invalidateQueries({
          queryKey: [namedOperations.Query.GetSiteTaxa],
        });
      } else {
        toast.error("Failed to add tag to taxa");
      }
      return addTagSuccessful;
    },

    [addTagToTaxa, selectedRecordsIDs, queryClient],
  );

  const removeTagFromSelectedRecords = useCallback(
    async (tagId: string) => {
      const res = await removeTagFromTaxa.mutateAsync({
        tagId,
        ids: selectedRecordsIDs,
      });

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Tests flake seeming to return nullable removeTagPlantMaterials
      const removeTagSuccessful = res?.removeTagTaxa?.success ?? false;

      if (removeTagSuccessful) {
        toast.success(`Tag removed from ${selectedRecordsIDs.length} taxa`);
        refetchTags();
        void queryClient.invalidateQueries({
          queryKey: [namedOperations.Query.GetTaxa],
        });
        void queryClient.invalidateQueries({
          queryKey: [namedOperations.Query.GetSiteTaxa],
        });
      } else {
        toast.error("Failed to remove tag from taxa");
      }
      return removeTagSuccessful;
    },
    [refetchTags, removeTagFromTaxa, selectedRecordsIDs, queryClient],
  );

  const hasTags = allTags.length > 0;

  return (
    <CommandMenuTagsPage
      allTags={allTags}
      shouldShowHeading={allTags.length > 0}
      selectedRecordsWithTags={activeRecordTags}
      setSelectedRecordsWithTags={setActiveRecordTags}
      hasTags={hasTags}
      search={search}
      clearSearch={clearSearch}
      applyTagToSelectedRecords={applyTagToSelectedRecords}
      removeTagFromSelectedRecords={removeTagFromSelectedRecords}
    />
  );
};

const CommandMenuCollectionSiteTagsPage = ({
  selectedRecordsIDs,
  ...props
}: Pick<
  CommandMenuTaxonTagsPageProps,
  "selectedRecordsIDs" | "search" | "clearSearch"
>) => {
  const { siteSlug } = useParams({ from: "/_layout/site/$siteSlug" });
  const { subdomain } = useRouteContext({ from: "/_layout/site/$siteSlug" });

  const [{ data, fetching, error }] = useGetSiteTaxaQuery({
    variables: {
      organisationSubdomain: subdomain,
      collectionSiteSlug: siteSlug,
      last: selectedRecordsIDs.length,
      filter: {
        id: {
          in: selectedRecordsIDs,
        },
      },
    },
  });

  const selectedRows = data?.org?.site?.result?.nodes ?? [];

  const [
    { data: tagsData, fetching: fetchingTags, error: tagsError },
    refetchTags,
  ] = useCollectionSiteTagsQuery({
    variables: {
      organisationSubdomain: subdomain,
      collectionSiteSlug: siteSlug,
      orgFilters: {
        hasCollectionSiteRelation: {
          eq: false,
        },
      },
    },
  });

  const orgTags = tagsData?.org?.tags?.nodes ?? [];
  const siteTags = tagsData?.org?.site?.tags?.nodes ?? [];

  const allTags = [...orgTags, ...siteTags].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  if (error != null || tagsError != null) {
    return <ErrorAlert message="Something went wrong." />;
  }

  if ((data == null && fetching) || (tagsData == null && fetchingTags)) {
    return <CommandLoading />;
  }

  return (
    <CommandMenuTaxonTagsPage
      {...props}
      selectedRecordsIDs={selectedRecordsIDs}
      selectedRows={selectedRows}
      allTags={allTags}
      refetchTags={refetchTags}
    />
  );
};

const CommandMenuOrganisationTagsPage = ({
  selectedRecordsIDs,
  ...props
}: Pick<
  CommandMenuTaxonTagsPageProps,
  "selectedRecordsIDs" | "search" | "clearSearch"
>) => {
  const { subdomain } = useRouteContext({ strict: false });
  const [{ data, fetching, error }] = useGetTaxaQuery({
    variables: {
      organisationSubdomain: subdomain ?? "",
      last: selectedRecordsIDs.length,
      filter: {
        id: {
          in: selectedRecordsIDs,
        },
      },
    },
    pause: subdomain == null,
  });

  const [
    { data: tagsData, fetching: fetchingTags, error: tagsError },
    refetchTags,
  ] = useOrganisationTagsQuery({
    variables: {
      organisationSubdomain: subdomain ?? "",
    },
    pause: subdomain == null,
  });

  const selectedRows = data?.org?.result?.nodes ?? [];
  const allTags = tagsData?.org?.tags?.nodes ?? [];

  if (error != null || tagsError != null) {
    return <ErrorAlert message="Something went wrong." />;
  }

  if ((data == null && fetching) || (tagsData == null && fetchingTags)) {
    return <CommandLoading />;
  }

  return (
    <CommandMenuTaxonTagsPage
      {...props}
      selectedRecordsIDs={selectedRecordsIDs}
      selectedRows={selectedRows}
      allTags={allTags}
      refetchTags={refetchTags}
    />
  );
};

export {
  CommandMenuCollectionSiteTagsPage,
  CommandMenuOrganisationTagsPage,
  CommandMenuTaxonTagsPage,
};
